import { requestRecordingPermission } from "@memorang/helpers/device";
import { useTranslation } from "react-i18next";
import SessionConfig from "../screens/SessionConfig";

import { events } from "@constants/tracking";
import { buildFrontendRecommendationOptions } from "@features/dashboard/recommendations";
import { useHydratedStudyRecommendations } from "@features/dashboard/recommendations/hooks/useHydratedStudyRecommendations";
import { useSession } from "@features/session/hooks/useSession";
/* -----------------Helpers & Hooks--------------- */
import { getParentMetadata } from "@helpers/content/metadata";
import useAnalytics from "@hooks/useAnalytics";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useStudyPackAccess } from "@hooks/useStudyPackAccess";
import type { Distribution } from "@memorang/types";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import Loader from "@memorang/ui/components/Loader";
import { MicrophonePermissionDialog } from "@memorang/ui/components/MicroPhonePermissionDialog";
import { useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";

import { useAppStore } from "@hooks/useAppStore";
import Box from "@memorang/ui/components/Box";
import { questionTypeIconMap, skillIconMap } from "@memorang/ui/constants";
import { useProficiencyIconMap } from "@memorang/ui/hooks/useProficiencyIconMap";
import { defaultDistributionFilterItems } from "../constants/options";
import {
	calculateProficiencyBasedDistribution,
	getSelectedSkills,
	mapTagItemsToProficiencyDistribution,
} from "../helpers/filter";
import { resolvePremiumTags } from "../helpers/premium-upsell";
import { usePremiumStudyPackTags } from "../hooks/usePremiumStudyPackTags";
import { useSessionConfigSections } from "../hooks/useSessionConfigSections";

const SessionConfigContainer = ({
	propSelectedTagIds,
	handleClose,
	propStudyRecommendedTopics,
	propPreferRecommendedTab,
	onShowPurchase,
	showTitle,
}: {
	propSelectedTagIds?: number[];
	handleClose?: () => void;
	propStudyRecommendedTopics?: boolean;
	propPreferRecommendedTab?: boolean;
	onShowPurchase: () => void;
	showTitle?: boolean;
}) => {
	const { t } = useTranslation();
	const { selectedTagIds, studyRecommendedTopics, preferRecommendedTab } =
		useLocalSearchParams<{
			selectedTagIds: string;
			studymore: string;
			studyRecommendedTopics?: string;
			preferRecommendedTab?: string;
		}>();

	const {
		showErrorDialog,
		toggleShowErrorDialog,
		isCreatingSession,
		handleCreateCommonSession,
	} = useSession();

	const finalSelectedTagIds = useMemo(
		() =>
			propSelectedTagIds ??
			(selectedTagIds
				? selectedTagIds.split(",").map((id) => Number(id.trim()))
				: []),
		[propSelectedTagIds, selectedTagIds],
	);
	const finalStudyRecommendedTopics =
		propStudyRecommendedTopics || studyRecommendedTopics === "true";
	const finalPreferRecommendedTab =
		propPreferRecommendedTab || preferRecommendedTab === "true";

	const showPointsBasedScore = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);

	const crossTagSelection = useAppStore(
		(state) => state.app.sessionConfig?.crossTagSelection,
	);

	const maxFilterDepth = useAppStore(
		(state) => state.app.sessionConfig?.maxFilterDepth ?? 1,
	);

	const {
		data: studyPackDistribution,
		isLoading: isDistributionLoading,
		isFetching: isDistributionFetching,
	} = useMixedItemDist({
		action: "GET_STUDYPACK_DISTRIBUTION",
	});

	const sessionConfigFilters = useAppStore(
		(state) =>
			state.app.sessionConfig?.filters || defaultDistributionFilterItems,
	);

	const memoizedAnalyticsParams = useMemo(() => {
		return {
			eventName: events.sessionConfigOpened,
			...(finalPreferRecommendedTab
				? {
						prefer_recommended_tab: "true",
						selected_tag_ids: finalSelectedTagIds.join(","),
					}
				: {}),
		};
	}, [finalPreferRecommendedTab, finalSelectedTagIds]);
	useAnalytics(memoizedAnalyticsParams, !isDistributionFetching);
	const { hasStudyPackContentAccess, isLoading: isStudyPackAccessLoading } =
		useStudyPackAccess();
	const hydrated = useHydratedStudyRecommendations({
		durationOptions: true,
		filterValueIds:
			finalPreferRecommendedTab && finalSelectedTagIds.length > 0
				? finalSelectedTagIds
				: undefined,
	});
	const proficiencyIconMap = useProficiencyIconMap();
	const studyRecommendationSessions = useMemo(
		() =>
			hydrated.status === "READY"
				? buildFrontendRecommendationOptions({
						result: hydrated.presentation,
						skills: hydrated.frontend.skills,
						proficiencies: hydrated.frontend.proficiencies,
						questionTypes: hydrated.frontend.questionTypes,
						translate: t,
						proficiencyIconMap,
						skillIconMap,
						questionTypeIconMap,
					}).map((option) => option.quickStudySession)
				: [],
		[hydrated, t, proficiencyIconMap],
	);

	const [showPermissionDialog, setShowPermissionDialog] = useState(false);

	const {
		mappedMixedItemDistResponse,
		tagsByType,
		sectionTags,
		groupSelectedTagsByType,
	} = useSessionConfigSections({
		studyPackDistribution,
		hasAccess: hasStudyPackContentAccess,
		maxFilterDepth,
		sessionConfigFilters,
		showPointsBasedScore,
	});
	const premiumStudyPackTags = usePremiumStudyPackTags();

	const data = mappedMixedItemDistResponse;
	const isLoadingSessionConfig =
		isDistributionLoading ||
		isDistributionFetching ||
		isStudyPackAccessLoading ||
		!data;

	if (isLoadingSessionConfig) {
		return (
			<Box flex={1}>
				<Box
					flex={1}
					justifyContent="center"
					alignItems="center"
					height={"100%"}
				>
					<Loader />
				</Box>
			</Box>
		);
	}

	const { contents = [], freeTagIds } = data;

	const premiumTagsByType = resolvePremiumTags({
		freeTags: studyPackDistribution?.tags,
		generalTags: premiumStudyPackTags,
	});

	const { distribution } = getParentMetadata(contents);

	const handleCreateSession = async (dist: Distribution, tags: number[]) => {
		const selectedSkills = getSelectedSkills(
			sectionTags.flatMap((section) => section.tags),
			tags,
		);
		if (selectedSkills.includes("SPEAKING")) {
			const granted = await requestRecordingPermission();
			if (!granted) {
				setShowPermissionDialog(true);
				return;
			}
		}

		const groupedTags = groupSelectedTagsByType(tags);
		const finalTags =
			crossTagSelection || groupedTags.length > 1 ? groupedTags : [tags];

		handleCreateCommonSession({
			dist,
			tags: finalTags,
			handleClose,
			itemIds: [],
			source: "session_config",
		});
	};

	const handleShowPurchase = () => {
		handleClose?.();
		onShowPurchase();
	};
	const numQuestions =
		contents?.find((item) => item.type === "QUESTION_SET")?.totalCount || 0;

	const writingSectionTags = sectionTags.filter(
		(s) => s.title.toLowerCase() === "writing",
	);
	const numEssays = writingSectionTags.reduce(
		(acc, section) =>
			acc +
			section.tags.reduce((tagAcc, tag) => tagAcc + (tag.numItems || 0), 0),
		0,
	);

	const finalDistribution =
		showPointsBasedScore && studyPackDistribution?.tags
			? calculateProficiencyBasedDistribution(
					studyPackDistribution?.tags,
					sessionConfigFilters,
				).accumulatedDistribution
			: distribution;
	const lockedRecommendationTags =
		studyPackDistribution?.tags.map((tag) => ({
			...tag,
			locked: !(hasStudyPackContentAccess || freeTagIds.includes(tag.id)),
		})) ?? [];
	const recommendationTags = showPointsBasedScore
		? mapTagItemsToProficiencyDistribution(lockedRecommendationTags)
		: lockedRecommendationTags;

	return (
		<>
			<SessionConfig
				available={finalDistribution}
				creatingSession={isCreatingSession}
				distributionFilterItems={sessionConfigFilters}
				handleCreateSession={handleCreateSession}
				handleShowPurchase={handleShowPurchase}
				handleClose={handleClose}
				showTitle={showTitle}
				hasAccess={hasStudyPackContentAccess}
				studyRecommendationEnabled={hydrated.status !== "DISABLED"}
				studyRecommendationLoading={hydrated.status === "LOADING"}
				studyRecommendationSessions={studyRecommendationSessions}
				exams={studyPackDistribution?.exams}
				additionalParams={{
					tagsByType,
					freeTagIds,
					hasAccess: hasStudyPackContentAccess,
					studyRecommendedTopics: finalStudyRecommendedTopics,
					preferRecommendedTab: finalPreferRecommendedTab,
					sectionTags,
					recommendationTags,
					selectedTagIds: finalSelectedTagIds,
					numItemsMap: {
						numQuestions,
						numEssays,
					},
					premiumSkills: studyPackDistribution?.premiumSkills,
					premiumTagsByType,
					premiumStudyPackTags,
				}}
			/>

			<MicrophonePermissionDialog
				showDialog={showPermissionDialog}
				setShowDialog={() => {
					setShowPermissionDialog(false);
				}}
			/>

			{showErrorDialog && (
				<ConfirmDialog
					visible
					title={t("errors.sessionCreation.title")}
					body={t("errors.sessionCreation.body")}
					dismissCta="Ok"
					dismissCallback={toggleShowErrorDialog}
				/>
			)}
		</>
	);
};

export default SessionConfigContainer;
